import React from 'react';

interface TimeDisplayProps {
    date: string | Date;
    prefix?: string;
    className?: string;
}

export const TimeDisplay: React.FC<TimeDisplayProps> = ({
    date,
    prefix = 'Publié',
    className = ''
}) => {
    const getRelativeDate = (date: Date | string) => {
        // Convert input to Date if it's a string
        const inputDate = date instanceof Date ? date : new Date(date);
        const today = new Date();

        // Calculate time difference in milliseconds
        const diffTime = inputDate.getTime() - today.getTime();
        const isPast = diffTime < 0;

        // If it's a future date, use the standard RTF
        if (!isPast) {
            const rtf = new Intl.RelativeTimeFormat("fr", { numeric: "auto" });
            const diffMinutes = Math.ceil(Math.abs(diffTime) / (1000 * 60));

            if (diffMinutes < 60) {
                return rtf.format(diffMinutes, "minutes");
            }

            const diffHours = Math.ceil(Math.abs(diffTime) / (1000 * 60 * 60));
            if (diffHours < 24) {
                return rtf.format(diffHours, "hours");
            }

            const diffDays = Math.ceil(Math.abs(diffTime) / (1000 * 60 * 60 * 24));
            return rtf.format(diffDays, "days");
        }

        // For past dates, use our custom formatting
        const diffMs = Math.abs(diffTime);
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffMonths = Math.floor(diffDays / 30);
        const diffYears = Math.floor(diffDays / 365);

        // Get days in the month when the ad was posted
        const daysInPostMonth = new Date(
            inputDate.getFullYear(),
            inputDate.getMonth() + 1,
            0
        ).getDate();

        // Format based on the appropriate time unit

        // If less than an hour, show minutes
        if (diffMinutes < 60) {
            if (diffMinutes === 0) {
                return "à l'instant";
            } else if (diffMinutes === 1) {
                return "il y'a 1 minute";
            }
            return `il y'a ${diffMinutes} minutes`;
        }

        // If less than a day, show hours
        if (diffHours < 24) {
            if (diffHours === 1) {
                return "il y'a 1 heure";
            }
            return `il y'a ${diffHours} heures`;
        }

        // If more than a year, show years
        if (diffYears > 0) {
            if (diffYears === 1) {
                return `il y'a 1 an`;
            }
            return `il y'a ${diffYears} ans`;
        }

        // If more than days in the posting month, show months
        if (diffDays > daysInPostMonth) {
            if (diffMonths === 1) {
                return `il y'a 1 mois`;
            }
            return `il y'a ${diffMonths} mois`;
        }

        // If less than a month, show days
        if (diffDays === 0) {
            // If today, show "aujourd'hui"
            return "aujourd'hui";
        } else if (diffDays === 1) {
            return `il y'a 1 jour`;
        }
        return `il y'a ${diffDays} jours`;
    };

    return (
        <div className={`text-content-primary font-medium text-xs ${className}`}>
            {`${prefix} ${getRelativeDate(date)}`.trim()}
        </div>
    );
};
